import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/** @type {boolean} */
let cartUpdateListenerBound = false;

/**
 * @param {string} sectionId
 */
function bindCartUpdateInvalidation(sectionId) {
  if (cartUpdateListenerBound) return;

  cartUpdateListenerBound = true;

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    sectionRenderer.invalidateAllSectionUrls(sectionId);
  });
}

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  bindCartUpdateInvalidation(normalizedId);

  const cartBeforeHydration = await fetch('/cart.js', { credentials: 'same-origin' })
    .then((response) => response.json())
    .catch(() => null);

  const drawerNeedsFill =
    cartBeforeHydration?.item_count &&
    !document.querySelector('cart-items-component[data-drawer] .cart-form');

  if (drawerNeedsFill) {
    const drawerCartItems = document.querySelector('cart-items-component[data-drawer]');
    if (drawerCartItems && typeof drawerCartItems.refreshDrawer === 'function') {
      await drawerCartItems.refreshDrawer(undefined, { fromAddToCart: true });
    }
    section.dataset.hydrated = 'true';
    return;
  }

  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  const cartAfterHydration = await fetch('/cart.js', { credentials: 'same-origin' })
    .then((response) => response.json())
    .catch(() => null);

  if (cartAfterHydration?.item_count && !document.querySelector('cart-items-component[data-drawer] .cart-form')) {
    const drawerCartItems = document.querySelector('cart-items-component[data-drawer]');
    if (drawerCartItems && typeof drawerCartItems.refreshDrawer === 'function') {
      await drawerCartItems.refreshDrawer(undefined, { fromAddToCart: true });
    }
  }

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}
